
/* ==========================================================================
   LOW-LEVEL UI STATE HELPERS (/database/ui-core.js)
   ========================================================================== */

import { deactivateAccessibleLayer } from './a11y.js?v=20260819-audit2';

export const cardPool = new Map();

export function clearCardPool() {
  cardPool.clear();
}

export function closeSpecNoteModal(box, { remove = false, restoreFocus = true } = {}) {
  if (!box) return;
  const toggle = box.id ? [...document.querySelectorAll('.spec-micro-toggle')].find(el => el.getAttribute('data-target') === box.id) : null;
  if (toggle) {
    toggle.innerText = "NOTES ▼";
    toggle.setAttribute('aria-expanded', 'false');
  }
  box.classList.remove('active');
  deactivateAccessibleLayer(box, { restoreFocus });
  if (remove) box.remove();
}

export function cleanupOrphanNoteModals() {
  document.querySelectorAll('.spec-dropdown-box').forEach(el => closeSpecNoteModal(el, { remove: true }));
}

export function dynamicSliderFill(slider) {
  if (!slider) return;
  const min = Number(slider.min || 0);
  const max = Number(slider.max || 100);
  const value = Number(slider.value || min);
  const range = Math.max(1e-9, max - min);
  const pct = Math.max(0, Math.min(100, ((value - min) / range) * 100));
  slider.style.background = `linear-gradient(to right,var(--sff-sky) 0%,var(--sff-sky) ${pct}%,var(--sff-slider-track) ${pct}%,var(--sff-slider-track) 100%)`;
}

export function updateSliderBadges() {
  document.querySelectorAll('.sff-slider').forEach(slider => {
    const unit = slider.id === 'maxVolume' ? 'L' : 'mm';
    const badge = document.getElementById(`badge-${slider.id}`);
    if (badge) badge.innerText = `${slider.value}${unit}`;
    dynamicSliderFill(slider);
  });

  const ramSummary = document.getElementById('sffRamSummary');
  if (ramSummary) {
    const height = Number(document.getElementById('ramHeight')?.value || 35);
    const modules = Math.max(1, Math.round(Number(document.getElementById('ramModules')?.value || 2)));
    ramSummary.textContent = `${Number.isFinite(height) ? Math.round(height * 10) / 10 : 35}MM · ${modules} DIMM${modules === 1 ? '' : 'S'}`;
  }
}

export function updateThemeToggleUI(isDark) {
  const iconContainer = document.getElementById('themeToggleIcon');
  const textContainer = document.getElementById('themeToggleText');
  if (isDark) {
    if (iconContainer) iconContainer.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="sff-sun-moon-svg"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>`;
    if (textContainer) textContainer.innerText = 'LIGHT MODE';
  } else {
    if (iconContainer) iconContainer.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="sff-sun-moon-svg"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>`;
    if (textContainer) textContainer.innerText = 'DARK MODE';
  }
}

export function setPcieSlotRequirement(slotValue) {
  const numeric = Number(slotValue);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;

  const normalized = Math.round(numeric * 100) / 100;
  const value = Number.isInteger(normalized) ? String(normalized) : String(normalized);
  const input = document.getElementById('gpuPcieSlots');
  if (input) input.value = value;

  const buttons = [...document.querySelectorAll('#gpuPcieSegmented .segment-btn')];
  buttons.forEach(button => {
    const buttonValue = Number(button.getAttribute('data-value'));
    const active = Number.isFinite(buttonValue) && Math.abs(buttonValue - normalized) < 0.001;
    button.classList.toggle('active', active);
    button.setAttribute('aria-pressed', String(active));
    button.setAttribute('aria-checked', String(active));
  });
  return normalized;
}

/**
 * Derive a coarse slot envelope from millimetre thickness. `clampToCase` is
 * only appropriate when creating a case-compatible default state. It must be
 * false for an explicitly selected GPU because user hardware requirements are
 * independent of the selected case's capability.
 */
export function syncPcieSlotsFromThickness(thickness, maxAllowedSlots = null, options = {}) {
  const { clampToCase = false, preferredSlots = null } = options || {};
  const preferred = Number(preferredSlots);
  if (Number.isFinite(preferred) && preferred > 0) {
    return setPcieSlotRequirement(preferred);
  }

  const t = Number(thickness);
  let targetSlot = 2;
  if (Number.isFinite(t)) {
    if (t <= 22) targetSlot = 1;
    else if (t <= 44) targetSlot = 2;
    else if (t <= 64) targetSlot = 3;
    else targetSlot = 4;
  }

  const maxSlots = Number(maxAllowedSlots);
  if (clampToCase && Number.isFinite(maxSlots) && maxSlots > 0) {
    targetSlot = Math.min(targetSlot, maxSlots);
  }
  return setPcieSlotRequirement(targetSlot);
}
